// src/components/BusinessIntelligenceStatus.tsx
// ✅ FIXED: Component for displaying $3B revenue business intelligence status

import React from 'react';
import { defensePlanningBusinessService, BusinessIntelligenceData } from '../../../services/businessIntelligenceService';

interface BusinessIntelligenceStatusProps {
  businessData: BusinessIntelligenceData | null;
  businessLoading: boolean;
  businessError: string | null;
  onRefresh: () => Promise<void> | void;
  riskScore?: { score: number; riskLevel: string };
  isFallbackData?: boolean;
}

const BusinessIntelligenceStatus: React.FC<BusinessIntelligenceStatusProps> = ({ 
  businessData,
  businessLoading,
  businessError,
  onRefresh,
  riskScore,
  isFallbackData = false
}) => {
  if (businessLoading) {
    return (
      <div role="status" aria-live="polite" aria-atomic="true" style={{
        padding: '15px',
        backgroundColor: 'rgba(0, 102, 255, 0.08)',
        borderRadius: '8px',
        border: '1px solid rgba(0, 102, 255, 0.2)',
        marginBottom: '20px',
        fontSize: '12px',
        color: 'var(--ra-cyber-aqua)'
      }}>
        <strong>🔄 Loading Business Intelligence:</strong> Extracting from assessment data...
      </div>
    );
  }

  if (businessError) {
    return (
      <div aria-live="polite" aria-atomic="true" style={{
        padding: '15px',
        backgroundColor: 'rgba(255, 165, 0, 0.08)',
        borderRadius: '8px',
        border: '1px solid rgba(255, 165, 0, 0.3)',
        marginBottom: '20px',
        fontSize: '12px',
        color: 'var(--ra-warning)'
      }}>
        <div><strong>⚠️ Business Intelligence Warning:</strong> {businessError}</div>
        <div style={{ marginTop: '5px' }}>Using fallback multipliers for friction cost calculations.</div>
        <button 
          onClick={onRefresh}
          style={{ 
            marginTop: '8px',
            background: 'none',
            border: '1px solid rgba(255, 165, 0, 0.3)',
            color: 'var(--ra-warning)',
            padding: '4px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px'
          }}
        >
          Retry Loading
        </button>
      </div>
    );
  }

  if (businessData) {
    // ✅ FIXED: Use the corrected friction calculation with actual business data
    const frictionData = defensePlanningBusinessService.calculateFrictionMultipliers(businessData);
    const companyRevenue = defensePlanningBusinessService.formatCurrency(frictionData.annualRevenue, 'auto');
    
    // ✅ Extract actual revenue for verification — preserve null (unmeasured) rather
    // than collapsing to 0, which formatCurrency would otherwise render as "$0.0M"
    // (a confident figure), inconsistent with the "Unmeasured" scaling factor below.
    const rawActualRevenue = businessData.actualRevenue?.amount;
    const actualRevenue = (rawActualRevenue === null || rawActualRevenue === undefined) ? null : rawActualRevenue;
    const displayRevenue = defensePlanningBusinessService.formatCurrency(actualRevenue, 'auto');
    
    
    return (
      <div aria-live="polite" aria-atomic="true" style={{
        padding: '25px',
        backgroundColor: isFallbackData ? 'rgba(255, 165, 0, 0.08)' : 'rgba(0, 255, 136, 0.08)',
        borderRadius: '12px',
        border: `2px solid ${isFallbackData ? 'rgba(255, 165, 0, 0.3)' : 'var(--ra-success)'}`,
        marginBottom: '25px',
        fontSize: '14px',
        color: isFallbackData ? 'var(--ra-warning)' : 'var(--ra-success)'
      }}>
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', color: isFallbackData ? 'var(--ra-warning)' : 'var(--ra-text-primary)' }}>
              {isFallbackData ? '⚠️ Business Intelligence (Fallback)' : '✅ Business Intelligence Active'}
            </h3>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
              {isFallbackData ? 'Using default values — API data unavailable' : 'Real Assessment Data Loaded'}
            </p>
          </div>
          <button 
            onClick={onRefresh}
            style={{ 
              background: 'none',
              border: '2px solid var(--ra-success)',
              color: 'var(--ra-success)',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            Refresh
          </button>
        </div>

        {/* Key Metrics Grid - Larger and more prominent */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '15px',
          marginBottom: '15px'
        }}>
          {/* Risk Level - Qualitative display */}
          {riskScore && (() => {
            const level = riskScore.riskLevel || (riskScore.score <= 20 ? 'Low' : riskScore.score <= 40 ? 'Medium' : riskScore.score <= 60 ? 'High' : 'Critical');
            const config: Record<string, { color: string; border: string; bg: string; desc: string }> = {
              Low: { color: '#22c55e', border: 'rgba(34, 197, 94, 0.25)', bg: 'rgba(34, 197, 94, 0.08)', desc: 'Risk posture is strong' },
              Medium: { color: '#fbbf24', border: 'rgba(251, 191, 36, 0.25)', bg: 'rgba(251, 191, 36, 0.08)', desc: 'Some areas need attention' },
              High: { color: '#f87171', border: 'rgba(248, 113, 113, 0.25)', bg: 'rgba(248, 113, 113, 0.08)', desc: 'Significant gaps identified' },
              Critical: { color: '#ef4444', border: 'rgba(239, 68, 68, 0.25)', bg: 'rgba(239, 68, 68, 0.08)', desc: 'Immediate action required' },
            };
            const c = config[level] || config.Medium;
            return (
              <div style={{ padding: '15px', backgroundColor: c.bg, borderRadius: '8px', border: `1px solid ${c.border}` }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '5px' }}>Risk Posture</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: c.color }}>{level}</div>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-muted)' }}>{c.desc}</div>
              </div>
            );
          })()}

          {/* Revenue at Risk */}
          <div style={{
            padding: '15px',
            backgroundColor: 'rgba(255, 68, 68, 0.08)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 68, 68, 0.25)'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '5px' }}>Revenue at Risk</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
              {businessData.revenueAtRisk && typeof businessData.revenueAtRisk === 'object' 
                ? (businessData.revenueAtRisk.formatted || 
                   (businessData.revenueAtRisk.amount ? `$${(businessData.revenueAtRisk.amount / 1000000).toFixed(1)}M` : 'N/A'))
                : String(businessData.revenueAtRisk || 'N/A')
              }
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Annual exposure</div>
          </div>
        </div>

        {/* Additional Details Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '12px',
          fontSize: '12px',
          opacity: 0.9
        }}>
          <div>
            <strong>Assessment ID:</strong> {String(businessData.assessmentId)}
          </div>
          <div>
            <strong>Company Revenue:</strong> {String(displayRevenue)}
          </div>
          <div>
            <strong>Revenue Scaling:</strong>{' '}
            {frictionData.scalingFactor === null ? 'Unmeasured' : `${frictionData.scalingFactor.toFixed(2)}x multiplier`}
          </div>
          <div>
            <strong>Data Source:</strong> Real Assessment API
          </div>
        </div>

        {/* ✅ Success indicator for $3B revenue */}
        {actualRevenue >= 1000000000 && (
          <div style={{ 
            marginTop: '8px',
            padding: '6px 10px',
            backgroundColor: 'rgba(0, 255, 136, 0.08)',
            borderRadius: '4px',
            border: '1px solid rgba(0, 255, 136, 0.15)',
            fontSize: '11px',
            fontWeight: 'bold'
          }}>
            🎯 Large Enterprise Revenue Detected: Dynamic friction scaling active for {displayRevenue} company
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default BusinessIntelligenceStatus;